import { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';

import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    Typography,
    ButtonGroup,
    Grid,
    Skeleton
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCardIcon from '@mui/icons-material/AddCard';
import RestoreIcon from '@mui/icons-material/Restore';
import UndoIcon from '@mui/icons-material/Undo';

import { formatCurrency } from '../utils';
import { formatToLongDate } from '../../utils/formatToLongDate';
import { chipOrderStatus } from '../../utils/chipOrderStatus';
import { chipPaymentStatus } from '../../utils/chipPaymentStatus';
import { useLockBodyScroll } from '../utils';

import { printListedOrders } from '../../utils/printListedOrders';
import { printMarkedOrder } from '../../utils/printMarkedOrder';

import SummaryCards from './SummaryCards';
import FilterListTab from './FilterListTab';

import CustomSearch from '../custom_components/CustomSearch';
import CustomButton from '../custom_components/CustomButton';
import CustomRefreshButton from '../custom_components/CustomRefreshButton';
import CustomIconButton from '../custom_components/CustomIconButton';
import CustomSelect from '../custom_components/CustomSelect';

import SearchNotFound from '../../utils/SearchNotFound';
import EditOrder from '../manage_orders/EditOrder';
import PaymentOrder from '../manage_payments/PaymentOrder';
import ItemQuantityReport from './ItemQuantityReport';

const ListViewer = ({ listInfo, onBack }) => {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [loading, setLoading] = useState(false);
    const [listOrders, setListOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [tabChange, setTabChange] = useState('');

    const keyword = searchTerm.toLowerCase();
    const searchOrder = listOrders.filter(order =>
        order.CustomerName.toLowerCase().includes(keyword) ||
        order.ItemName.toLowerCase().includes(keyword)
    );

    const [openReport, setOpenReport] = useState(false);
    const [openEditOrder, setOpenEditOrder] = useState(false);
    const [openPaymentOrder, setOpenPaymentOrder] = useState(false);
    const [orderInfo, setOrderInfo] = useState({});

    const [printingAllMarked, setPrintingAllMarked] = useState(false);
    const [printingMarkedOrder, setPrintingMarkedOrder] = useState(null);
    const [loadingMarkOrder, setLoadingMarkOrder] = useState(null);
    const [loadingOrderStat, setLoadingOrderStat] = useState(null);

    const [selectedItem, setSelectedItem] = useState("");

    useLockBodyScroll(openEditOrder);
    useLockBodyScroll(openPaymentOrder);
    useLockBodyScroll(openReport);

    // DISPLAY LISTED ORDERS
    const handleDisplayListOrder = async () => {
        setLoading(true);
        setListOrders([]);

        try {
            const response = await fetch(`${apiUrl}manage_list/get_order_list.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    listID: listInfo.ListID
                })
            });

            const result = await response.json();

            if (result.status === 'success') {

                setListOrders(result.data || []);

            } else if (result.status === 'error') {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message,
                    icon: 'error'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error,
                icon: 'error'
            });
            console.error('Error getting orders:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleDisplayListOrder();
    }, []);

    // GROUP DATA BY ITEMS
    const groupedData = useMemo(() => {
        const uniqueMap = new Map();

        for (const order of searchOrder) {

            const isVisible =
                tabChange === "" ||

                (tabChange === "Marked" &&
                    order.Remarks === "Marked" &&
                    order.OrderStat !== "Cancelled") ||

                (tabChange === "In Transit" &&
                    order.Remarks === "In_Transit" &&
                    order.OrderStat !== "Cancelled") ||

                (tabChange === "Cancelled" &&
                    order.OrderStat === "Cancelled");

            // Filter by selected item
            const matchesItem =
                selectedItem === "" ||
                order.ItemName === selectedItem;

            if (!isVisible || !matchesItem) continue;

            uniqueMap.set(order.OrderID, order);
        }

        const grouped = {};

        for (const order of uniqueMap.values()) {
            const key = order.ItemName || "Others";

            if (!grouped[key]) {
                grouped[key] = [];
            }

            grouped[key].push(order);
        }

        return grouped;

    }, [searchOrder, tabChange, selectedItem]);

    // SHOW ADD OR EDIT
    const handleAddEditOrder = (info, type) => {
        setOrderInfo(info);

        const actions = {
            edit: () => setOpenEditOrder(true),
            add_payment: () => setOpenPaymentOrder(true),
        };

        actions[type]?.();
    };

    // PRINT A LIST
    const handlePrintList = () => {
        const list_title =
            `${listInfo.ListName} ${formatToLongDate(listInfo.DateCreated).toUpperCase()}`;
        printListedOrders(listOrders, list_title);
    };

    const handleMarkedOrder = async (order) => {
        const result = await Swal.fire({
            icon: 'question',
            title: 'Mark Order?',
            text: 'This order will be marked as ready for shippment.',
            showCancelButton: true,
            confirmButtonText: 'Yes, Mark It',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
        });

        if (result.isConfirmed) {
            await handleRemarkOrder(order, "Marked");
        }
    };

    // FUNCTION PRINT ALL TO PRINT ORDERS 
    const handlePrintAllMarkedOrders = async () => {
        const markedOrders = listOrders.filter(o => o.Remarks === "Marked");

        if (markedOrders.length === 0) {
            Swal.fire({
                text: 'Please marked order/s before printing.',
                icon: 'info'
            });
            return;
        }

        // if (!labelPrinter?.trim()) {
        //     Swal.fire({
        //         icon: 'warning',
        //         title: 'Printer Required',
        //         text: 'Please configure a Label Printer in Printer Settings.'
        //     });
        //     return;
        // }

        setPrintingAllMarked(true);

        const success = await handleRemarkOrder(markedOrders, "In_Transit");
        if (!success) return;

        await printMarkedOrder(markedOrders);

        setPrintingAllMarked(false);
    };

    // PRINT SINGLE MARKED ORDER 
    const handlePrintLabel = async (order) => {
        if (order.Remarks !== "Marked" && order.Remarks !== "In_Transit") {
            Swal.fire({
                text: 'Order must be marked before printing.',
                icon: 'info',
            });
            return;
        }

        // if (!labelPrinter?.trim()) {
        //     Swal.fire({
        //         icon: 'warning',
        //         title: 'Printer Required',
        //         text: 'Please configure a Label Printer in Printer Settings.'
        //     });
        //     return;
        // }

        if (order.Remarks === "Marked" || order.Remarks === "In_Transit") {
            setPrintingMarkedOrder(order.OrderID);

            const success = await handleRemarkOrder(order, "In_Transit");
            if (!success) {
                setPrintingMarkedOrder(null);
                return;
            }
        }

        // ✅ Print order
        await printMarkedOrder([order]);

        setPrintingMarkedOrder(null);
    };

    const handleUndoMark = (order) => {
        handleRemarkOrder(order, "Marked");
    };

    // FUNCTION REMARK ORDER
    const handleRemarkOrder = async (orders, type) => {
        const orderArray = Array.isArray(orders)
            ? orders
            : [orders];

        try {

            if (orderArray.length === 1) {
                setLoadingMarkOrder(orderArray[0].OrderID);
            }

            const response = await fetch(`${apiUrl}manage_list/remark_listed_order.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orders: orderArray,
                    type: type,
                }),
            });

            const result = await response.json();

            if (result.status === 'success') {

                const orderIds = orderArray.map(
                    order => order.OrderID
                );

                setListOrders(prev =>
                    updateOrderRemarks(
                        prev,
                        orderIds,
                        type
                    )
                );

                return true;
            }

            if (result.status === 'error') {
                await Swal.fire({
                    text: result.message || 'Failed to update order.',
                    icon: 'error',
                });

                return false;
            }

            await Swal.fire({
                title: 'Unexpected Response',
                text: 'The server returned an unknown status.',
                icon: 'warning',
            });

            return false;

        } catch (error) {
            console.error('Error updating order:', error);

            await Swal.fire({
                title: 'Server Error!',
                text: error.message || 'An error occurred while updating order.',
                icon: 'error',
            });

            return false;
        } finally {
            setLoadingMarkOrder(null);
        }
    };

    // UPDATE REMARKS
    const updateOrderRemarks = (
        orders,
        orderIds,
        remarks
    ) => {

        const ids = Array.isArray(orderIds)
            ? orderIds
            : [orderIds];

        return orders.map(order =>
            ids.includes(order.OrderID)
                ? {
                    ...order,
                    Remarks: remarks,
                    OrderStat:
                        remarks === "In_Transit"
                            ? "In_Transit"
                            : order.OrderStat,
                }
                : order
        );
    };

    // CANCEL AND UNDO CANCEL
    const handleOrderStatus = (order, status) => {

        const isCancel = status === "Cancelled";

        Swal.fire({
            title: isCancel
                ? "Cancel this order?"
                : "Restore this order?",
            html: isCancel
                ? `The order <b>${order.ItemName}</b> for <b>${order.CustomerName}</b> will be marked as cancelled. You can restore it later.`
                : `The order <b>${order.ItemName}</b> for <b>${order.CustomerName}</b> will be restored and become active again.`,
            icon: isCancel ? "warning" : "question",
            showCancelButton: true,
            confirmButtonText: isCancel
                ? "Yes, cancel it"
                : "Yes, restore it",
            cancelButtonText: "Keep Current Status",
            reverseButtons: true,
            confirmButtonColor: isCancel ? "#DC2626" : "#2563EB",
        }).then((result) => {
            if (result.isConfirmed) {
                updateOrderStatus(order.CustomerID, order.OrderID, status);
            }
        });
    };

    // UPDATE ORDER STATUS
    const updateOrderStatus = async (customerId, orderId, orderStat) => {

        setLoadingOrderStat(orderId);

        try {

            const response = await fetch(`${apiUrl}manage_orders/cancel_order.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    customerID: customerId,
                    orderID: orderId,
                    orderStat,
                }),
            }
            );

            const result = await response.json();

            if (result.stat === "success") {

                await Swal.fire({
                    icon: "info",
                    text: result.message,
                    timer: 2500,
                    showConfirmButton: false,
                });

                setListOrders(prev =>
                    prev.map(item =>
                        item.OrderID === orderId &&
                            item.CustomerID === customerId
                            ? {
                                ...item,
                                OrderStat: orderStat,
                            }
                            : item
                    )
                )

                return;
            }

            Swal.fire({
                icon: "error",
                text: result.message,
            });

        } catch (error) {
            console.error(error);
            Swal.fire({
                title: "Server Error",
                text: error.message || "Something went wrong.",
                icon: "error",
            });
        } finally {
            setLoadingOrderStat(null);
        }
    };

    // SELECT BY ORDERS OPTIONS
    const itemOptions = useMemo(() => [
        { label: <em>All</em>, value: "" },
        ...Array.from(
            new Set(searchOrder.map(order => order.ItemName))
        )
            .filter(Boolean)
            .sort()
            .map(item => ({
                label: item,
                value: item,
            })),
    ], [searchOrder]);

    const computeOrderAmount = (item) => {
        return (
            (Number(item?.Price || 0) *
                Number(item?.ItemQty || 0)) +
            Number(item?.SFee || 0)
        );
    };

    return (
        <Box>

            {/* HEADER AND TITLE */}
            <Stack flexDirection="row" gap={2} alignItems="center">
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: '#1a1a1a',
                        letterSpacing: 0.2,
                    }}
                >
                    {listInfo.ListName}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#757575',
                        fontWeight: 500,
                    }}
                >
                    {formatToLongDate(listInfo.DateCreated)}
                </Typography>
            </Stack>

            {/* SEARCH AND BUTTONS */}
            <Stack flexDirection="row" justifyContent="space-between" mt={2} mb={1}>

                <Stack flexDirection="row" gap={2}>
                    <CustomSearch
                        md='46ch'
                        placeholder='Search by Customer Name, Item Name'
                        value={searchTerm}
                        onChange={(val) => setSearchTerm(val)}
                    />
                    
                    <CustomRefreshButton
                        label="Refresh"
                        loading={loading}
                        onClick={() => handleDisplayListOrder()}
                    />

                    <CustomSelect
                        label="Filter by Item"
                        minWidth={250}
                        value={selectedItem}
                        onChange={setSelectedItem}
                        options={itemOptions}
                    />
                </Stack>

                <Stack direction="row" gap={2}>
                    <ButtonGroup direction='row' gap={2}>
                        <CustomButton
                            label="Report"
                            onClick={() => setOpenReport(true)}
                        />
                        <CustomButton
                            label="Print List"
                            onClick={handlePrintList}
                        />
                        <CustomButton
                            label="Print All Marked"
                            loading={printingAllMarked}
                            disabled={printingAllMarked}
                            onClick={() => handlePrintAllMarkedOrders(listOrders)}
                        />
                    </ButtonGroup>

                    <CustomButton
                        label="Back"
                        variant="borderless"
                        hoverColor="#1976d2"
                        startIcon={<ArrowBackIosIcon />}
                        onClick={onBack}
                    />
                </Stack>

            </Stack>

            <FilterListTab tabChange={(e) => setTabChange(e)} listedOrders={listOrders} />

            <Stack
                sx={{
                    p: 0,
                    pr: 1,
                    mt: 2,
                    maxHeight: 620,
                    overflowY: 'auto',
                }}
            >
                {loading ? (

                    <Grid container spacing={3}>
                        {[...Array(2)].map((_, groupIndex) => (
                            <Grid size={12} key={groupIndex}>
                                <Stack
                                    sx={{
                                        borderRadius: 1,
                                        border: "1px solid #e0e0e0",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* HEADER */}
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            backgroundColor: "#1C2536",
                                        }}
                                    >
                                        <Skeleton
                                            variant="text"
                                            width={180}
                                            height={28}
                                            sx={{
                                                bgcolor: "rgba(255,255,255,.15)",
                                            }}
                                        />

                                        <Skeleton
                                            variant="rounded"
                                            width={32}
                                            height={24}
                                            sx={{
                                                bgcolor: "rgba(255,255,255,.15)",
                                            }}
                                        />
                                    </Box>

                                    {/* TABLE HEADER */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "1.4fr 1.4fr .8fr .5fr .8fr .6fr .8fr .8fr 1fr",
                                            gap: 2,
                                            p: 1.5,
                                            bgcolor: "#1C2536",
                                        }}
                                    >
                                        {[...Array(9)].map((_, i) => (
                                            <Skeleton
                                                key={i}
                                                variant="text"
                                                width="80%"
                                                height={18}
                                                sx={{
                                                    bgcolor: "rgba(255,255,255,.12)",
                                                }}
                                            />
                                        ))}
                                    </Box>

                                    {/* SKELETON TABLE ROWS */}
                                    {[...Array(3)].map((_, rowIndex) => (
                                        <Box
                                            key={rowIndex}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns:
                                                    "1.4fr 1.4fr .8fr .5fr .8fr .6fr .8fr .8fr 1fr",
                                                gap: 2,
                                                p: 1.5,
                                                borderTop: "1px solid #f1f5f9",
                                                alignItems: "center",
                                            }}
                                        >
                                            {/* Customer */}
                                            <Stack spacing={0.5}>
                                                <Skeleton width="70%" height={20} />
                                                <Skeleton width="45%" height={14} />
                                            </Stack>

                                            {/* Item */}
                                            <Skeleton width="90%" height={20} />

                                            {/* Price */}
                                            <Skeleton width="60%" height={20} />

                                            {/* Qty */}
                                            <Skeleton width="40%" height={20} />

                                            {/* Shipping */}
                                            <Skeleton width="60%" height={20} />

                                            {/* Cargo */}
                                            <Skeleton width="50%" height={20} />

                                            {/* Paid */}
                                            <Skeleton width="70%" height={20} />

                                            {/* Status */}
                                            <Skeleton
                                                variant="rounded"
                                                width={80}
                                                height={24}
                                            />

                                            {/* Actions */}
                                            <Stack direction="row" spacing={1}>
                                                <Skeleton
                                                    variant="circular"
                                                    width={28}
                                                    height={28}
                                                />
                                                <Skeleton
                                                    variant="circular"
                                                    width={28}
                                                    height={28}
                                                />
                                                <Skeleton
                                                    variant="circular"
                                                    width={28}
                                                    height={28}
                                                />
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>

                ) : Object.entries(groupedData).length > 0 ? (
                    Object.entries(groupedData).map(([itemName, rows]) => {
                        const totalQty = rows.reduce(
                            (total, row) => total + (Number(row.ItemQty) || 0),
                            0
                        );

                        return (
                            <Stack
                                key={itemName}
                                mb={4}
                                sx={{
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                }}
                            >
                                {/* HEADER */}
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1,

                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",

                                        position: "relative",
                                        overflow: "hidden",

                                        borderRadius: "8px 8px 0 0",
                                        borderBottom: "1px solid #2B3648",

                                        background: "#1C2536",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#F8FAFC",
                                            letterSpacing: 0.3,
                                        }}
                                    >
                                        {itemName}
                                    </Typography>

                                    <Box
                                        sx={{
                                            minWidth: 28,
                                            height: 24,

                                            px: 1,

                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",

                                            borderRadius: 0.8,

                                            backgroundColor: "rgba(255,255,255,.15)",
                                            color: "#FFFFFF",

                                            fontSize: 11,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {`${totalQty} ORDER${totalQty === 1 ? "" : "S"}`}
                                    </Box>
                                </Box>

                                {/* ORDERS */}
                                <TableContainer
                                    key={itemName}
                                    sx={{
                                        border: "none",
                                        borderRadius: 0,
                                    }}
                                >
                                    <Table size="small" key={itemName}>
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    'Customer',
                                                    'Item',
                                                    'Shipping Fee',
                                                    'Item Amount',
                                                    'Paid Amount',
                                                    'Balance',
                                                    'Payment Status',
                                                    'Order Status',
                                                    'Actions',
                                                ].map((header, index) => (
                                                    <TableCell key={header} align={header === "Actions" ? "center" : "left"} sx={{
                                                        bgcolor: "#1C2536",
                                                        color: "#94A3B8",
                                                        fontWeight: 700,
                                                        fontSize: 11,
                                                        letterSpacing: "0.6px",
                                                        textTransform: "uppercase",
                                                        borderBottom: "none",
                                                        py: 1,
                                                        width: header === "#" ? 48 : header === "Actions" ? 110 : "auto",
                                                    }}>
                                                        {header}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody key={itemName}>
                                            {rows.map((order) => {
                                                const isMarked = order.Remarks === "Marked";
                                                const isInTransit = order.Remarks === "In_Transit";

                                                const isVisibleInTab =
                                                    tabChange === "" ||
                                                    tabChange === "Cancelled" ||
                                                    (tabChange === "Marked" && isMarked) ||
                                                    (tabChange === "In Transit" && isInTransit);

                                                const showCancelButton =
                                                    isVisibleInTab &&
                                                    order.OrderStat !== "Cancelled";

                                                const showRestoreButton =
                                                    isVisibleInTab &&
                                                    order.OrderStat === "Cancelled";

                                                const showUndoButton =
                                                    isVisibleInTab &&
                                                    tabChange !== "" &&
                                                    tabChange !== "Marked" &&
                                                    order.OrderStat !== "Cancelled";

                                                const itemBalance =
                                                    Number(computeOrderAmount(order)) -
                                                    Number(order.PaidAmount);

                                                return (
                                                    <TableRow
                                                        key={order.OrderID}
                                                        sx={{
                                                            cursor: "pointer",
                                                            transition: "all .2s ease",
                                                            "&:hover": {
                                                                backgroundColor: "#dcfbff",
                                                            },
                                                        }}
                                                    >
                                                        {/* CUSTOMER */}
                                                        <TableCell>
                                                            <Stack spacing={0.1}>
                                                                <Typography
                                                                    fontWeight={700}
                                                                >
                                                                    {order.CustomerName}
                                                                </Typography>

                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    {order.SecondName}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>

                                                        {/* ITEM */}
                                                        <TableCell>
                                                            <Stack spacing={0.5}>
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                    sx={{ lineHeight: 1.2 }}
                                                                >
                                                                    {order.ItemName}
                                                                </Typography>

                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="#15803D"
                                                                        fontWeight={600}
                                                                    >
                                                                        {formatCurrency(order.Price)}
                                                                    </Typography>

                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        Qty: {order.ItemQty}
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </TableCell>

                                                        {/* SHIPPING */}
                                                        <TableCell>
                                                            <Stack alignItems="left" spacing={0.5}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 12,
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {formatCurrency(order.SFee)}
                                                                </Typography>

                                                                <Typography
                                                                    sx={{
                                                                        width: 32,
                                                                        px: 1,
                                                                        py: 0.25,
                                                                        borderRadius: 0.5,
                                                                        fontSize: 10,
                                                                        fontWeight: 600,
                                                                        textAlign: "center",
                                                                        whiteSpace: "nowrap",
                                                                        backgroundColor:
                                                                            order.Cargo === "SC"
                                                                                ? "#DCFCE7" // Green
                                                                                : "#DBEAFE", // Blue (AC)
                                                                        color:
                                                                            order.Cargo === "SC"
                                                                                ? "#15803D"
                                                                                : "#1D4ED8",
                                                                    }}
                                                                >
                                                                    {order.Cargo}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>

                                                        {/* ITEM AMOUNT */}
                                                        <TableCell>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 13,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {formatCurrency(computeOrderAmount(order))}
                                                            </Typography>
                                                        </TableCell>

                                                        {/* PAID AMOUNT */}
                                                        <TableCell>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 13,
                                                                    fontWeight: 600,
                                                                    color: Number(order.PaidAmount) > 0
                                                                        ? '#059669'
                                                                        : '#94A3B8',
                                                                }}
                                                            >
                                                                {Number(order.PaidAmount) > 0
                                                                    ? formatCurrency(order.PaidAmount)
                                                                    : <em>No DP</em>}
                                                            </Typography>
                                                        </TableCell>

                                                        {/* ITEM BALANCE */}
                                                        <TableCell>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 13,
                                                                    fontWeight: 600,
                                                                    color: Number(itemBalance) > 0
                                                                        ? '#c71f1f'
                                                                        : '#94A3B8',
                                                                }}
                                                            >
                                                                {Number(itemBalance) > 0
                                                                    ? formatCurrency(itemBalance)
                                                                    : <em>Fully Paid</em>}
                                                            </Typography>
                                                        </TableCell>

                                                        {/* PAYMENT STATUS */}
                                                        <TableCell>
                                                            {chipPaymentStatus(order.PayStatus || "Pending")}
                                                        </TableCell>

                                                        {/* ORDER STATUS */}
                                                        <TableCell>
                                                            {chipOrderStatus(order.OrderStat)}
                                                        </TableCell>

                                                        {/* ACTIONS */}
                                                        <TableCell align="center">
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="center"
                                                                spacing={1}
                                                            >
                                                                {/* EDIT ORDER */}
                                                                <CustomIconButton
                                                                    type="success"
                                                                    icon={<EditIcon fontSize="small" />}
                                                                    onClick={() => handleAddEditOrder(order, "edit")}
                                                                />

                                                                {/* ADD PAYMENT FOR ORDER */}
                                                                <CustomIconButton
                                                                    type="info"
                                                                    icon={<AddCardIcon fontSize="small" />}
                                                                    onClick={() => handleAddEditOrder(order, "add_payment")}
                                                                />

                                                                {/* MARK ORDER */}
                                                                {tabChange === "" &&
                                                                    order.Remarks !== "Marked" &&
                                                                    order.OrderStat !== "In_Transit" && (
                                                                        <CustomIconButton
                                                                            type="view"
                                                                            loading={loadingMarkOrder === order.OrderID}
                                                                            icon={<BookmarkIcon fontSize="small" />}
                                                                            onClick={() => handleMarkedOrder(order)}
                                                                        />
                                                                    )}

                                                                {/* PRINT MARKED ORDER */}
                                                                {(tabChange === "Marked" &&
                                                                    order.Remarks === "Marked") ||
                                                                    (tabChange === "In Transit" &&
                                                                        order.Remarks === "In_Transit") ? (
                                                                    <CustomIconButton
                                                                        type="info"
                                                                        loading={printingMarkedOrder === order.OrderID}
                                                                        icon={<PrintIcon fontSize="small" />}
                                                                        onClick={() => handlePrintLabel(order)}
                                                                    />
                                                                ) : null}

                                                                {/* UNDO PRINTED ORDER */}
                                                                {showUndoButton && (
                                                                    <CustomIconButton
                                                                        type="view"
                                                                        loading={loadingMarkOrder === order.OrderID}
                                                                        icon={<UndoIcon fontSize="small" />}
                                                                        onClick={() => handleUndoMark(order)}
                                                                    />
                                                                )}

                                                                {/* CANCEL ORDER */}
                                                                {showCancelButton && (
                                                                    <CustomIconButton
                                                                        type="delete"
                                                                        loading={loadingOrderStat === order.OrderID}
                                                                        icon={<CancelIcon fontSize="small" />}
                                                                        onClick={() => handleOrderStatus(order, "Cancelled")}
                                                                    />
                                                                )}

                                                                {/* UNDO CANCELLED ORDER */}
                                                                {showRestoreButton && (
                                                                    <CustomIconButton
                                                                        type="warning"
                                                                        loading={loadingOrderStat === order.OrderID}
                                                                        icon={<RestoreIcon fontSize="small" />}
                                                                        onClick={() => handleOrderStatus(order, "Confirmed")}
                                                                    />
                                                                )}

                                                            </Stack>
                                                        </TableCell>

                                                    </TableRow>
                                                )
                                            }
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                            </Stack>
                        )
                    })
                ) : (
                    <SearchNotFound />
                )}
            </Stack>

            {/* SUMMARIES */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                }}
            >
                <SummaryCards orders={listOrders} />
            </Box>

            <EditOrder
                open={openEditOrder}
                onClose={() => setOpenEditOrder(false)}

                orderInfo={orderInfo}
                onUpdate={(val) =>
                    setListOrders(prev =>
                        prev.map(item =>
                            item.OrderID === val.orderID &&
                                item.CustomerID === val.customerID
                                ? {
                                    ...item,
                                    ItemName: val.ItemName,
                                    ItemQty: val.ItemQty,
                                    ItemCategory: val.ItemCategory,
                                    Price: val.Price,
                                    SFee: val.SFee,
                                    Cargo: val.Cargo,
                                }
                                : item
                        )
                    )
                }
            />

            <PaymentOrder
                open={openPaymentOrder}
                onClose={() => setOpenPaymentOrder(false)}

                orderInfo={orderInfo}
                onUpdate={(val) => setListOrders(prev =>
                    prev.map(item =>
                        item.OrderID === val.OrderID &&
                            item.CustomerID === val.CustomerID
                            ? {
                                ...item,
                                PaidAmount: val.PaidAmount,
                            }
                            : item
                    )
                )}
                onUpdateStatus={(val) => setListOrders(prev =>
                    prev.map(item =>
                        item.OrderID === val.OrderID &&
                            item.CustomerID === val.CustomerID
                            ? {
                                ...item,
                                PayStatus: val.payStatus,
                            }
                            : item
                    )
                )}
            />

            <ItemQuantityReport
                open={openReport}
                onClose={() => setOpenReport(false)}
                listInfo={listInfo}
                data={listOrders}
            />

        </Box>
    );
};

export default ListViewer;
